import Box from '@mui/material/Box';
import {
  DatePicker as MuiDatePicker,
  type DatePickerProps as MuiDatePickerProps,
} from '@mui/x-date-pickers/DatePicker';

import { InfoIconButton } from '../InfoIconButton';

export type DatePickerProps = {
  infoTooltip?: string;
  fullWidth?: boolean;
} & MuiDatePickerProps;

type DatePickerSlotProps = NonNullable<MuiDatePickerProps['slotProps']>;

const labelClearanceSx = {
  '&:not(.MuiInputLabel-shrink)': {
    maxWidth: 'calc(100% - 72px)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
};

const withLabelClearance = (textFieldSlotProps: DatePickerSlotProps['textField']): DatePickerSlotProps['textField'] => {
  return (ownerState) => {
    const resolvedTextFieldSlotProps =
      typeof textFieldSlotProps === 'function' ? textFieldSlotProps(ownerState) : textFieldSlotProps;
    const inputLabelSlotProps = resolvedTextFieldSlotProps?.slotProps?.inputLabel;

    return {
      ...resolvedTextFieldSlotProps,
      slotProps: {
        ...resolvedTextFieldSlotProps?.slotProps,
        inputLabel: {
          ...inputLabelSlotProps,
          sx: [
            labelClearanceSx,
            ...(Array.isArray(inputLabelSlotProps?.sx) ? inputLabelSlotProps.sx : [inputLabelSlotProps?.sx]),
          ],
        },
      },
    };
  };
};

export const DatePicker = ({
  infoTooltip,
  fullWidth = false,
  // No default: let MUI derive the format from the active adapterLocale (set from the
  // user's locale) so the picker matches the localized date display. Callers can still
  // pass an explicit `format` to override.
  format,
  slotProps,
  ...rest
}: DatePickerProps) => {
  const resolvedSlotProps = {
    ...slotProps,
    textField: withLabelClearance(slotProps?.textField),
  };

  return infoTooltip ? (
    <Box
      sx={{
        display: 'flex',
        gap: 1,
      }}
    >
      <MuiDatePicker
        format={format}
        {...rest}
        slotProps={resolvedSlotProps}
        sx={{ width: fullWidth ? '100%' : undefined }}
      />
      <InfoIconButton tooltip={infoTooltip} sx={{ marginTop: 1 }} />
    </Box>
  ) : (
    <MuiDatePicker format={format} {...rest} slotProps={resolvedSlotProps} />
  );
};
